import React from "react";
import "./styles.scss";

/**
 * Returns page numbers plus "…" markers, e.g. [1, "…", 5, 6, 7, "…", 11]
 * @param {number} currentPage - 1-based current page
 * @param {number} totalPages - total number of pages
 * @param {number} siblingCount - pages rendered either side of current (default 1)
 * @returns {Array} array of page numbers and "…" strings
 */
export function getPageRange(currentPage, totalPages, siblingCount = 1) {
  if (totalPages <= 5 + siblingCount * 2) {
    // All pages fit, no ellipsis needed
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const result = [];
  const leftSibling = Math.max(1, currentPage - siblingCount);
  const rightSibling = Math.min(totalPages, currentPage + siblingCount);

  // Always include first page
  result.push(1);

  // Check gap between 1 and leftSibling
  // Gap = leftSibling - 1 - 1 = leftSibling - 2
  // If gap >= 2, add ellipsis; otherwise render all pages from 2 to leftSibling
  const leftGap = leftSibling - 2;
  if (leftGap >= 2) {
    result.push("…");
    // Add pages starting from leftSibling
    for (let i = leftSibling; i <= rightSibling; i++) {
      result.push(i);
    }
  } else {
    // gap < 2 (gap is 0 or 1): render all pages from 2 to rightSibling
    for (let i = 2; i <= rightSibling; i++) {
      result.push(i);
    }
  }

  // Check gap between rightSibling and totalPages
  // Gap = totalPages - rightSibling - 1
  // If gap >= 2, add ellipsis; otherwise render all remaining pages
  const rightGap = totalPages - rightSibling - 1;
  if (rightGap >= 2) {
    result.push("…");
    result.push(totalPages);
  } else if (rightGap > 0) {
    // gap == 1: render the single page between rightSibling and totalPages
    for (let i = rightSibling + 1; i < totalPages; i++) {
      result.push(i);
    }
    result.push(totalPages);
  } else if (rightSibling < totalPages) {
    // gap == 0: already adjacent, just add totalPages if not already there
    result.push(totalPages);
  }

  return result;
}

function Pagination({
  currentPage,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50],
  itemLabel = "items",
  siblingCount = 1,
  isLoading = false,
  disabled = false,
  className = "",
}) {
  const totalPages = Math.ceil(totalItems / pageSize);

  // Return nothing when totalItems === 0
  if (totalItems === 0) {
    return null;
  }

  // Return nothing when totalPages <= 1 and onPageSizeChange is absent
  if (totalPages <= 1 && !onPageSizeChange) {
    return null;
  }

  // Derive aria-label from itemLabel
  // Assuming itemLabel is like "employees" or "products"
  // Convert to title case and format as "{Item list} pagination"
  const itemLabelCapitalized = itemLabel.charAt(0).toUpperCase() + itemLabel.slice(1);
  const ariaLabel = `${itemLabelCapitalized.replace(/s$/, "")} list pagination`;

  // Calculate page range for page buttons
  const pageRange = getPageRange(currentPage, totalPages, siblingCount);

  // Calculate indices for the count display
  const fromIndex = (currentPage - 1) * pageSize + 1;
  const toIndex = Math.min(currentPage * pageSize, totalItems);

  // Handle page change
  const handlePageChange = (page) => {
    if (!disabled && !isLoading && page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  // Only render navigation if totalPages > 1
  const showNavigation = totalPages > 1;

  return (
    <nav
      className={`pager ${isLoading ? "pager--loading" : ""} ${className}`.trim()}
      aria-label={ariaLabel}
    >
      {/* Count line with status role for screen reader announcements */}
      <p className="pager__count" role="status">
        {isLoading ? (
          <>
            Loading {itemLabel} <b>{fromIndex}</b>–<b>{toIndex}</b>…
          </>
        ) : totalItems === 1 ? (
          <>
            Showing 1 of 1 {itemLabel}
          </>
        ) : (
          <>
            Showing <b>{fromIndex}</b>–<b>{toIndex}</b> of <b>{totalItems}</b> {itemLabel}
          </>
        )}
      </p>

      {/* Full page list - visible >= 641px */}
      {showNavigation && (
        <ul className="pager__pages">
          {/* Previous button */}
          <li>
            <button
              type="button"
              className="pager__btn"
              aria-label="Previous page"
              disabled={currentPage === 1 || disabled}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              ‹
            </button>
          </li>

          {/* Page buttons */}
          {pageRange.map((pageOrEllipsis, index) => {
            if (pageOrEllipsis === "…") {
              return (
                <li key={`ellipsis-${index}`}>
                  <span className="pager__ellipsis" aria-hidden="true">
                    …
                  </span>
                </li>
              );
            }
            const pageNum = pageOrEllipsis;
            return (
              <li key={pageNum}>
                <button
                  type="button"
                  className="pager__btn"
                  aria-label={
                    pageNum === currentPage
                      ? `Page ${pageNum}, current page`
                      : `Page ${pageNum}`
                  }
                  aria-current={pageNum === currentPage ? "page" : undefined}
                  disabled={disabled}
                  onClick={() => handlePageChange(pageNum)}
                >
                  {pageNum}
                </button>
              </li>
            );
          })}

          {/* Next button */}
          <li>
            <button
              type="button"
              className="pager__btn"
              aria-label="Next page"
              disabled={currentPage === totalPages || disabled}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              ›
            </button>
          </li>
        </ul>
      )}

      {/* Compact view - visible <= 640px */}
      {showNavigation && (
        <span className="pager__compact">
          <button
            type="button"
            className="pager__btn"
            aria-label="Previous page"
            disabled={currentPage === 1 || disabled}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            ‹
          </button>
          <span>
            Page <b>{currentPage}</b> of {totalPages}
          </span>
          <button
            type="button"
            className="pager__btn"
            aria-label="Next page"
            disabled={currentPage === totalPages || disabled}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            ›
          </button>
        </span>
      )}

      {/* Size control - omitted when onPageSizeChange is absent */}
      {onPageSizeChange && (
        <span className="pager__size">
          <label htmlFor="pager-select" className="pager__label">
            Show
          </label>
          <select
            id="pager-select"
            className="pager__select"
            value={pageSize}
            onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
            disabled={disabled}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </span>
      )}
    </nav>
  );
}

export { Pagination };
